/**
 * ServersPanel — registrace externích GitLab serverů (acm:admin).
 *
 * Widget-first: custom widget content bez vnějšího Card.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Server } from 'lucide-react';
import { api } from '../../../lib/api';
import { Select } from '../../../components/ui/Select';
import { Badge, Button, Input } from '../../../components/ui';
import type { WidgetRenderProps } from '../../../widgets/types';

interface ServersResp {
  ok: boolean;
  total: number;
  servers: Array<{
    id: string;
    host: string;
    has_token: boolean;
    access?: { mode: 'direct' | 'owner'; owner_node_id?: string };
    credential_required_here?: boolean;
    delegation?: { issuer: string; provider: string; subject_claim: string };
    node?: string | null;
    instance_name?: string | null;
    kind?: 'acm' | 'external' | 'github';
    type?: 'gitlab' | 'github';
  }>;
}

/** Verze externího GitLabu z gitlab.versions_available (`external`). */
interface ExternalVersion {
  name: string;
  current_version: string | null;
  severity?: 'critical' | 'available' | 'current';
  recommended: string | null;
}

export function ServersPanel(_: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [conn, setConn] = useState({ id: 'gitlab-main', host: '', token: '', kind: 'gitlab' as 'gitlab' | 'github' });

  const serversQ = useQuery<ServersResp>({
    queryKey: ['gitlab-connections'],
    queryFn: () => api.mcp('gitlab.connections_list', {}, { silent: true }),
    staleTime: 30_000,
  });

  // Externí GitLab (mimo ACM) modul taky hlídá: verze a jestli je oprava kritická.
  const versionsQ = useQuery<{ external?: ExternalVersion[] }>({
    queryKey: ['gitlab-versions-external'],
    queryFn: () => api.mcp('gitlab.versions_available', { limit: 1 }, { silent: true }),
    staleTime: 10 * 60_000,
  });
  const externalVersion = new Map((versionsQ.data?.external ?? []).map((v) => [v.name, v]));

  const [route, setRoute] = useState<'direct' | 'owner'>('direct');
  const [owner, setOwner] = useState('');
  const [issuer, setIssuer] = useState('');
  const [provider, setProvider] = useState('openid_connect');
  const [claim, setClaim] = useState('sub');

  const connMut = useMutation({
    mutationFn: () => api.mcp('gitlab.connection_set', { ...conn, token: conn.token || undefined,
      ...(conn.kind === 'gitlab' ? { access: { mode: route, ...(route === 'owner' ? { owner_node_id: owner } : {}) },
        ...(issuer ? { delegation: { issuer, provider, subject_claim: claim } } : {}) } : {}) }),
    onSuccess: (r: unknown) => {
      const res = r as { ok?: boolean; error?: string };
      if (res?.ok === false) {
        toast.error(res.error ?? t('gitlab.toastServerFailed'));
        return;
      }
      toast.success(t('gitlab.toastServerSaved'));
      setConn({ ...conn, token: '', kind: conn.kind });
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['gitlab-connections'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const servers = serversQ.data?.servers ?? [];

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between text-xs">
        <span className="text-zinc-400">{t('gitlab.servers', { count: servers.length })}</span>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-zinc-400 hover:text-zinc-200"
        >
          {open ? t('common.hide') : t('gitlab.registerNew')}
        </button>
      </div>

      {servers.length === 0
        ? <p className="text-xs text-zinc-500">{t('gitlab.noServers')}</p>
        : (
          <div className="space-y-1">
            {servers.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-2 text-sm border border-zinc-800 rounded-lg px-3 py-2"
              >
                <span className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <Badge variant="blue">{s.id}</Badge>
                    <span className="text-zinc-400 text-xs truncate">{s.host}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    {s.type === 'github' || s.kind === 'github'
                      ? <Badge variant="purple">GitHub</Badge>
                      : s.kind === 'acm'
                      ? (
                        <Badge variant="purple">
                          {t('gitlab.kindAcm', 'ACM instance')}
                          {s.instance_name && s.instance_name !== s.id ? `: ${s.instance_name}` : ''}
                        </Badge>
                      )
                      : (
                        <Badge variant="gray">{t('gitlab.kindExternal', 'external')}</Badge>
                      )}
                    {(() => {
                      const v = s.kind === 'external' ? externalVersion.get(s.id) : undefined;
                      if (!v?.current_version) return null;
                      return (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-400">
                          {v.current_version.replace(/-ce\.0$/, '')}
                          {v.severity === 'critical'
                            ? <Badge variant="red">{t('gitlab.instances.versionAsap')}</Badge>
                            : v.severity === 'available'
                            ? <Badge variant="yellow">{t('gitlab.instances.versionNewer')}</Badge>
                            : <span className="text-zinc-600">{t('gitlab.instances.versionCurrent')}</span>}
                          {v.severity !== 'current' && v.recommended && (
                            <span>→ {v.recommended.replace(/-ce\.0$/, '')}</span>
                          )}
                        </span>
                      );
                    })()}
                    {s.node && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-violet-300">
                        <Server size={11} />
                        <span className="font-mono">{s.node}</span>
                      </span>
                    )}
                  </span>
                </span>
                <button className="text-xs text-sky-300" onClick={() => {
                  setConn({ id: s.id, host: s.host, token: '', kind: s.type === 'github' ? 'github' : 'gitlab' });
                  setRoute(s.access?.mode ?? 'direct'); setOwner(s.access?.owner_node_id ?? '');
                  setIssuer(s.delegation?.issuer ?? ''); setProvider(s.delegation?.provider ?? 'openid_connect');
                  setClaim(s.delegation?.subject_claim ?? 'sub'); setOpen(true);
                }}>{t('common.edit', 'Upravit')}</button>
                {s.access?.mode === 'owner' && <span className="text-xs text-violet-300">{t('gitlab.route.owner', 'Přes node')}: {s.access.owner_node_id}</span>}
                <Badge variant={s.has_token || s.credential_required_here === false ? 'green' : 'yellow'}>
                  {s.credential_required_here === false ? t('gitlab.route.routed', 'Pověření u vlastníka') : s.has_token ? t('gitlab.hasToken') : t('gitlab.noToken')}
                </Badge>
              </div>
            ))}
          </div>
        )}

      {open && (
        <div className="border-t border-zinc-800 pt-3 space-y-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConn({ ...conn, kind: 'gitlab', id: conn.kind === 'github' ? 'gitlab-main' : conn.id })}
              className={`text-xs px-2 py-1 rounded border ${
                conn.kind === 'gitlab'
                  ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
              }`}
            >
              GitLab
            </button>
            <button
              type="button"
              onClick={() => setConn({
                ...conn,
                kind: 'github',
                id: conn.kind === 'gitlab' ? 'github-main' : conn.id,
                host: conn.host || 'https://github.com',
              })}
              className={`text-xs px-2 py-1 rounded border ${
                conn.kind === 'github'
                  ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
              }`}
            >
              GitHub
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input
              placeholder={conn.kind === 'github' ? t('gitlab.idPlaceholderGithub') : t('gitlab.idPlaceholder')}
              value={conn.id}
              onChange={(e) => setConn({ ...conn, id: e.target.value })}
            />
            <Input
              placeholder={conn.kind === 'github' ? t('gitlab.hostPlaceholderGithub') : t('gitlab.hostPlaceholder')}
              value={conn.host}
              onChange={(e) => setConn({ ...conn, host: e.target.value })}
            />
            <Input
              type="password"
              placeholder={conn.kind === 'github' ? t('gitlab.patPlaceholderGithub') : t('gitlab.patPlaceholder')}
              value={conn.token}
              onChange={(e) => setConn({ ...conn, token: e.target.value })}
            />
          </div>
          {conn.kind === 'gitlab' && <div className="space-y-2">
            <Select value={route} onChange={(e) => setRoute(e.target.value as 'direct' | 'owner')}>
              <option value="direct">{t('gitlab.route.direct', 'Externí GitLab — přímý přístup tohoto nodu')}</option>
              <option value="owner">{t('gitlab.route.ownerOption', 'GitLab obsluhovaný jedním ACM nodem')}</option>
            </Select>
            {route === 'owner' && <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder={t('gitlab.route.nodeId', 'Neměnné UUID obsluhujícího nodu')} />}
            <p className="text-xs text-zinc-400">{t('gitlab.route.help', 'Token zadej pouze na nodu, který bude GitLab volat. Ostatní nody používají jeho službu na základě kontraktu.')}</p>
            <Input value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder={t('gitlab.route.issuer', 'SSO issuer (HTTPS adresa realmu)')} />
            <Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder={t('gitlab.route.provider', 'Název SSO poskytovatele v GitLabu')} />
            <Select value={claim} onChange={(e) => setClaim(e.target.value)}>
              <option value="sub">sub</option><option value="email">email</option><option value="preferred_username">preferred_username</option>
            </Select>
          </div>}
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              loading={connMut.isPending}
              disabled={!conn.id || !conn.host || (conn.kind === 'gitlab' && route === 'owner' && !owner)}
              onClick={() => connMut.mutate()}
            >
              {t('gitlab.saveServer')}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500">{t('gitlab.serverHelp')}</p>
        </div>
      )}
    </div>
  );
}
